import { FC } from "react";
import { Link } from "react-router-dom";
import withPage from "../../components/Page";
import Section from "../../components/Section";
import Button from "../../components/Button";
import Hero from "../../components/Hero";
import CtaSection from "../../components/CtaSection";
import { globalContent } from "../../config/content";
import styles from "./AI.module.scss";

const AI: FC = () => {
  return (
    <div className={styles.aiPage}>
      {/* 1. Hero */}
      <Hero
        eyebrow="AI for Building Information"
        heading="Ask questions about your building."
        copy="Pulse AI is designed to help people interact with the information behind a building. Ask questions about assets, documents, drawings and technical information and get useful answers instantly without trawling through 100+ page documents."
        backgroundImage={require("../../assets/images/ai/ai-information.webp")}
        button={{
          text: "Explore the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />

      {/* 2. The Problem */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>The Challenge</span>
            <h2 className={styles.sectionHeading}>
              Your building already has the answers. Finding them is the hard
              part.
            </h2>
            <p className={styles.sectionCopy}>
              A building can contain thousands of pieces of technical
              information—including O&M manuals, drawings, asset records,
              specifications, certificates and maintenance procedures.
            </p>
            <p className={styles.sectionCopy}>
              The challenge is knowing where the information is, which document
              contains it, and how everything fits together. AI provides another
              way to interact with that information.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/ai/ai-information-flow.webp")}
              alt="Building information flow into AI"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 3. AI That Knows Your Building */}
      <Section bgColor="white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/ai/ai-building-context.webp")}
              alt="AI contextual knowledge"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Building Context</span>
            <h2 className={styles.sectionHeading}>
              AI built around your building information.
            </h2>
            <p className={styles.sectionCopy}>
              Generic AI knows about the world. Pulse AI is intended to work
              with the information about <strong>your building</strong>. When
              you ask who manufactured AHU-003, the answer comes directly from
              the building's own records.
            </p>
          </div>
        </div>
      </Section>

      {/* 4. Ask Questions in Plain English */}
      <Section bgColor="off-white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Natural Language</span>
          <h2 className={styles.sectionHeading}>
            Ask questions instead of learning where everything is stored.
          </h2>
          <p className={styles.sectionCopy}>
            Users don't need to trawl through tonnes of information to find what
            they need.
          </p>
        </div>
        <div className={styles.questionCardGrid}>
          <div className={styles.questionCard}>
            "What is the recommended filter replacement interval for AHU-003?"
          </div>
          <div className={styles.questionCard}>
            "How do I clean the ductwork?"
          </div>
          <div className={styles.questionCard}>
            "Who manufactured the pumps serving this system?"
          </div>
          <div className={styles.questionCard}>
            "When does the warranty for AHU-003 expire?"
          </div>
        </div>
      </Section>

      {/* 5. Answers Grounded in Your Information */}
      <Section bgColor="white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Reliable Sources</span>
          <h2 className={styles.sectionHeading}>
            Useful AI needs reliable sources.
          </h2>
        </div>
        <div className={styles.groundedBox}>
          <div className={styles.gbHeader}>Question</div>
          <div className={styles.gbQuestion}>
            What is the recommended filter replacement interval for AHU-003?
          </div>
          <div className={styles.gbAnswer}>
            Based on the equipment specifications, filters should be inspected
            quarterly and replaced every 6 months or when pressure drop exceeds
            250 Pa.
          </div>
          <div className={styles.gbSourcesTitle}>Sources</div>
          <ul className={styles.gbSourcesList}>
            <li>AHU-003 O&M Manual (Page 42)</li>
            <li>AHU-003 Maintenance Procedure</li>
            <li>Manufacturer Datasheet</li>
          </ul>
        </div>
      </Section>

      {/* 6. Ask AI From Anywhere in Pulse */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Contextual AI</span>
            <h2 className={styles.sectionHeading}>AI where you need it.</h2>
            <p className={styles.sectionCopy}>
              The Ask AI experience sits alongside existing Pulse navigation.
              Whether viewing an asset, document or drawing, users can ask
              questions directly about the information currently on screen.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/ai/ai-contextual.webp")}
              alt="Contextual AI panel in Pulse"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 7. Ask About Assets */}
      <Section bgColor="white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/ai/ai-asset-intelligence.webp")}
              alt="Asset questions interface"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Asset Intelligence</span>
            <h2 className={styles.sectionHeading}>
              Ask questions about your assets.
            </h2>
            <p className={styles.sectionCopy}>
              Interact with asset records naturally. Ask about manufacturer
              information, operation instructions, how-tos, and so on.
            </p>
            <div className={styles.actionLink}>
              <Link to="/asset-information" className={styles.textLink}>
                Explore Asset Information →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 8. Ask About Documents & Drawings */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Technical Documents</span>
            <h2 className={styles.sectionHeading}>
              Ask questions about technical documents.
            </h2>
            <p className={styles.sectionCopy}>
              AI helps users understand complex documentation, answering
              questions about operating requirements and system relationships.
            </p>
            <div className={styles.actionLink}>
              <Link to="/documents-drawings" className={styles.textLink}>
                Explore Documents & Drawings →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/ai/ai-technical-documents.webp")}
              alt="Document questions interface"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 9. Summarise Technical Information */}
      <Section bgColor="white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/ai/ai-technical-summary.webp")}
              alt="Technical summary interface"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Summarisation</span>
            <h2 className={styles.sectionHeading}>
              Turn long technical information into something easier to
              understand.
            </h2>
            <p className={styles.sectionCopy}>
              Quickly generate summaries of lengthy O&M manuals, asset records
              and technical specifications while keeping source links visible.
            </p>
          </div>
        </div>
      </Section>

      {/* 10. Find Information Across Multiple Sources */}
      {/* @todo: consider adding back later if functionality is/becomes true */}
      {/* <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Multi-Source Synthesis</span>
            <h2 className={styles.sectionHeading}>
              Bring information together from across the building record.
            </h2>
            <p className={styles.sectionCopy}>
              Answering complex questions often requires data from asset
              records, manuals, maintenance guides and drawings simultaneously.
              AI synthesises these sources while keeping references transparent.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/placeholder.webp")}
              alt="Multi-source AI synthesis"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section> */}

      {/* 11. Sources Matter */}
      {/* @todo: consider adding later */}
      {/* <Section bgColor="white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/placeholder.webp")}
              alt="Source attribution interface"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Traceability</span>
            <h2 className={styles.sectionHeading}>
              Keep the answer connected to the source.
            </h2>
            <p className={styles.sectionCopy}>
              AI should help people get to information, not hide where it came
              from. Pulse AI provides direct links back to underlying documents,
              drawings and records for verification.
            </p>
          </div>
        </div>
      </Section> */}

      {/* 12. AI + Search */}
      <Section bgColor="off-white">
        <div className={styles.workflowBox}>
          <h3>
            Search when you know what you're looking for. Ask AI when you don't.
          </h3>
          <div className={styles.workflowSteps}>
            <div className={styles.workflowStep}>
              <div className={styles.stepNumber}>1</div>
              <div>
                <strong>Search:</strong> Locate specific asset or document
                references directly.
              </div>
            </div>
            <div className={styles.workflowStep}>
              <div className={styles.stepNumber}>2</div>
              <div>
                <strong>Ask AI:</strong> Ask natural questions when exploring
                unfamiliar or complex technical information.
              </div>
            </div>
            <div className={styles.workflowStep}>
              <div className={styles.stepNumber}>3</div>
              <div>
                <strong>Verify:</strong> Explore underlying source documents and
                drawings with complete confidence.
              </div>
            </div>
          </div>
          <div style={{ marginTop: "2rem" }}>
            <Link
              to="/search"
              className={styles.textLink}
              style={{ color: "var(--color-yellow)" }}
            >
              Explore Search →
            </Link>
          </div>
        </div>
      </Section>

      {/* 13. AI on Site */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>On-Site AI</span>
            <h2 className={styles.sectionHeading}>
              Put building knowledge in people's hands.
            </h2>
            <p className={styles.sectionCopy}>
              Facilities professionals standing beside equipment can use Pulse
              on mobile devices to ask questions and instantly retrieve manuals
              and maintenance procedures on site.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/ai/ai-on-site.webp")}
              alt="Engineer using Pulse on mobile"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 14. Future Possibilities */}
      {/* @todo: consider adding later */}
      {/* <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/placeholder.webp")}
              alt="Future AI capabilities"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Platform Evolution</span>
            <h2 className={styles.sectionHeading}>
              AI can become more useful as the building record grows.
            </h2>
            <p className={styles.sectionCopy}>
              As structured building data expands, Pulse evolves toward advanced
              building queries, maintenance tracking, technical document
              understanding and automated reporting capabilities.
            </p>
          </div>
        </div>
      </Section> */}

      {/* 15. Security & Information Boundaries */}
      {/* @todo: consider adding later */}
      {/* <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Information Boundaries</span>
            <h2 className={styles.sectionHeading}>
              AI should work within the building's information boundaries.
            </h2>
            <p className={styles.sectionCopy}>
              Building information is commercially sensitive. Pulse AI is
              designed around secure access, user authentication,
              building-specific information and controlled sources without
              treating the AI as an unrestricted generic chatbot.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/placeholder.webp")}
              alt="Security and information boundaries"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section> */}

      {/* 16. Benefits */}
      <Section bgColor="off-white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Benefits</span>
          <h2 className={styles.sectionHeading}>
            Make building information easier to understand and use.
          </h2>
        </div>
        <div className={styles.benefitGrid}>
          <div className={styles.benefitCard}>
            <h3>Ask questions naturally</h3>
            <p>
              Ask questions about building information using everyday language.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Understand documents</h3>
            <p>
              Ask questions about documents and information you're viewing in
              Pulse.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Get grounded answers</h3>
            <p>
              Receive answers based on the building information available in
              Pulse.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Understand technical information</h3>
            <p>
              Summarise and explain technical information in clearer, more
              accessible language.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Explore information more easily</h3>
            <p>
              Use AI to explore information without having to navigate through
              every detail manually.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Get help with complex information</h3>
            <p>
              Break down detailed building information into clearer, more
              manageable answers.
            </p>
          </div>
        </div>
      </Section>

      {/* 17. Product Showcase */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Product Showcase</span>
            <h2 className={styles.sectionHeading}>See AI in Pulse.</h2>
            <p className={styles.sectionCopy}>
              Experience how building information, search, sources and AI work
              together seamlessly within Pulse.
            </p>
            <div className={styles.actionLink}>
              <Button to={globalContent.demoUrl} variant="primary" size="md">
                Explore the Demo →
              </Button>
            </div>
          </div>
          <div
            className={styles.splitImageContainer}
            style={{
              background: "transparent",
              border: "none",
              boxShadow: "none",
              overflow: "visible",
            }}
          >
            <div className={styles.phoneFrame}>
              <div className={styles.phoneStatusBar}>
                <span>9:41</span>
                <div className={styles.statusDots}>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
              <div className={styles.phoneHeader}>
                <span className={styles.backArrow}>‹</span>
                <span>Ask AI</span>
              </div>
              <div className={styles.phoneBody}>
                <div className={styles.phoneContextCard}>
                  <div className={styles.contextTitle}>Context: AHU-003</div>
                  <div className={styles.contextSub}>
                    Air Handling Unit · Plant Room 01
                  </div>
                  <div className={styles.contextMeta}>
                    6 documents · 2 drawings · 14 asset records
                  </div>
                </div>
                <div className={styles.phoneUserBubble}>
                  What maintenance does this unit require?
                </div>
                <div className={styles.phoneAiBubble}>
                  <div className={styles.aiTextBold}>
                    AHU-003 requires quarterly filter inspection and an annual
                    service.
                  </div>
                  <div className={styles.aiTextRegular}>
                    The next planned task is the quarterly filter inspection,
                    due 14 October 2026. The annual service includes fan belt
                    condition, damper operation and controls checks.
                  </div>
                  <div className={styles.aiTags}>
                    <span className={styles.aiTag}>Maintenance schedule</span>
                    <span className={styles.aiTag}>O&M manual</span>
                    <span className={styles.aiTag}>Asset data</span>
                  </div>
                </div>
                <div className={styles.phoneUserBubble}>
                  Where is the filter access panel?
                </div>
                <div className={styles.phoneAiBubble}>
                  <div
                    className={styles.aiTextRegular}
                    style={{ marginBottom: 0 }}
                  >
                    The filter access panel is on the south side of the unit,
                    facing the plant-room entrance. See drawing M-PL01-200 for
                    the access clearance.
                  </div>
                </div>
              </div>
              <div className={styles.phoneInputArea}>
                <div className={styles.phoneInputPlaceholder}>
                  Ask a question about AHU-003...
                </div>
                <div className={styles.phoneSendBtn}>↑</div>
              </div>
              <div className={styles.phoneNav}>
                <div className={styles.navItem}>
                  <span className={styles.navIcon}>⌂</span>
                  <span>Home</span>
                </div>
                <div className={styles.navItem}>
                  <span className={styles.navIcon}>🔍</span>
                  <span>Search</span>
                </div>
                <div className={`${styles.navItem} ${styles.active}`}>
                  <span className={styles.navIcon}>✦</span>
                  <span>Ask AI</span>
                </div>
                <div className={styles.navItem}>
                  <span className={styles.navIcon}>📋</span>
                  <span>Documents</span>
                </div>
                <div className={styles.navItem}>
                  <span className={styles.navIcon}>☰</span>
                  <span>More</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 18. Final CTA */}
      <CtaSection
        eyebrow="Get Started"
        heading="Make your building information easier to understand."
        copy="Pulse brings your building's documents, drawings, assets and technical information together so AI can become a useful interface to the information you already have."
        button={{
          text: "Explore the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />
    </div>
  );
};

export default withPage(AI)({
  meta: globalContent.seo.ai,
});
